/**
 * StorageService.ts
 * Robust client-side persistence engine for American Dream: Pocket Life.
 * Features:
 * - Primary: HTML5 IndexedDB ('AmericanDreamDB', store 'gameState')
 * - Secondary: Synchronous localStorage fallback & snapshot mirror
 * - Multi-trigger auto-save (1s debounce, 10s heartbeat, pagehide/visibilitychange)
 * - Attenuated offline delta rehydration (max 8 hours, 70% attenuation)
 */

import {
  FullGameState,
  IStorageService,
  OfflineRehydrationResult
} from '../core/Types';
import { migrateGameState } from './Migrations';

export const DB_NAME = 'AmericanDreamDB';
export const DB_VERSION = 1;
export const STORE_NAME = 'gameState';
export const SAVE_KEY = 'current_save';
export const LOCAL_STORAGE_KEY = 'AmericanDreamDB_current_save';

export const MAX_OFFLINE_SECONDS = 28800; // 8 hours = 28,800s
export const OFFLINE_ATTENUATION_FACTOR = 0.30; // 70% attenuation -> 30% of normal decay

export function createDefaultGameState(): FullGameState {
  const now = Date.now();
  const todayStr = new Date(now).toISOString().slice(0, 10);
  return {
    version: 1,
    lastActiveTimestamp: now,
    stats: {
      money: 120,
      energy: 100,
      hunger: 100,
      happiness: 100,
      lastTickTimestamp: now,
    },
    activeBuffs: [],
    placedFurniture: [
      {
        id: 'starter_bed',
        typeId: 'bed',
        tier: 1,
        gridCol: 1,
        gridRow: 1,
        rotation: 0,
      },
      {
        id: 'starter_fridge',
        typeId: 'fridge',
        tier: 1,
        gridCol: 0,
        gridRow: 5,
        rotation: 0,
      },
      {
        id: 'starter_desk',
        typeId: 'desk',
        tier: 1,
        gridCol: 5,
        gridRow: 0,
        rotation: 0,
      },
      {
        id: 'starter_sofa',
        typeId: 'sofa',
        tier: 1,
        gridCol: 5,
        gridRow: 5,
        rotation: 0,
      },
    ],
    inventory: {
      foodItems: [
        { foodId: 'food_ny_slice', count: 2 },
      ],
      unlockedSouvenirs: ['souvenir_ny_statue'],
    },
    states: {
      NY: {
        stateId: 'NY',
        unlockedAt: now,
        visitCount: 1,
        masteryLevel: 'BRONZE',
      },
    },
    visitedAtlasStates: {
      NY: {
        code: 'NY',
        unlockedAt: now,
        visitCount: 1,
      },
    },
    visitedCaCounties: {},
    career: {
      currentJobId: 'unemployed',
      currentTitle: 'Đang tìm việc',
      currentTierIndex: 0,
      completedCertificates: [],
      totalShiftsCompleted: 0,
      accountCreatedAt: now,
      dailyShiftsDate: todayStr,
      dailyShiftsCompleted: 0,
      certModuleProgress: {},
    },
    customPlaceMappings: {
      cafe: 'CA',
      office: 'NY',
      gym: 'TX',
      mart: 'FL',
      park: 'WA',
    },
    lastGpsFix: undefined,
    offlineArrivalQueue: [],
  };
}

export function applyOfflineRehydration(state: FullGameState): {
  state: FullGameState;
  result: OfflineRehydrationResult;
} {
  const now = Date.now();
  let lastActive = Number(state.lastActiveTimestamp);

  if (!Number.isFinite(lastActive)) {
    lastActive = now;
  }

  const rawElapsedMs = now - lastActive;

  if (rawElapsedMs <= 0) {
    state.lastActiveTimestamp = now;
    state.stats.lastTickTimestamp = now;
    return {
      state,
      result: {
        elapsedSeconds: 0,
        clampedSeconds: 0,
        hungerDrained: 0,
        energyDrained: 0,
        happinessDrained: 0,
        expiredBuffCount: 0,
      },
    };
  }

  const rawElapsedSec = Math.floor(rawElapsedMs / 1000);
  const clampedSec = Math.min(rawElapsedSec, MAX_OFFLINE_SECONDS);
  const elapsedMinutes = clampedSec / 60;

  // Normal decay: Hunger -2.5%/min, Energy -2.0%/min
  // With 70% attenuation, rate is multiplied by OFFLINE_ATTENUATION_FACTOR (0.30)
  const hungerDrain = +(2.5 * elapsedMinutes * OFFLINE_ATTENUATION_FACTOR).toFixed(2);
  const energyDrain = +(2.0 * elapsedMinutes * OFFLINE_ATTENUATION_FACTOR).toFixed(2);

  const initialHunger = state.stats.hunger;
  const initialEnergy = state.stats.energy;
  const initialHappiness = state.stats.happiness;

  const hungerFloor = Math.min(initialHunger, 10);
  const energyFloor = Math.min(initialEnergy, 10);

  state.stats.hunger = Math.max(hungerFloor, +(initialHunger - hungerDrain).toFixed(2));
  state.stats.energy = Math.max(energyFloor, +(initialEnergy - energyDrain).toFixed(2));

  // Dynamic happiness coupling: if hunger or energy drained low (< 20%), drain happiness
  let happinessDrain = 0;
  if (state.stats.hunger < 20 || state.stats.energy < 20) {
    happinessDrain = +(1.5 * elapsedMinutes * OFFLINE_ATTENUATION_FACTOR).toFixed(2);
    const happinessFloor = Math.min(initialHappiness, 10);
    state.stats.happiness = Math.max(happinessFloor, +(initialHappiness - happinessDrain).toFixed(2));
  }

  // Adjust active buff expiration timers
  const initialBuffCount = state.activeBuffs.length;
  state.activeBuffs = state.activeBuffs.filter((b) => b.expiresAt > now);
  const expiredBuffCount = initialBuffCount - state.activeBuffs.length;

  state.lastActiveTimestamp = now;
  state.stats.lastTickTimestamp = now;

  return {
    state,
    result: {
      elapsedSeconds: rawElapsedSec,
      clampedSeconds: clampedSec,
      hungerDrained: +(initialHunger - state.stats.hunger).toFixed(2),
      energyDrained: +(initialEnergy - state.stats.energy).toFixed(2),
      happinessDrained: +(initialHappiness - state.stats.happiness).toFixed(2),
      expiredBuffCount,
    },
  };
}

export class StorageService implements IStorageService {
  private db: IDBDatabase | null = null;
  private isUsingFallback: boolean = false;
  private cachedState: FullGameState | null = null;
  private debounceTimer: ReturnType<typeof setTimeout> | null = null;
  private heartbeatInterval: ReturnType<typeof setInterval> | null = null;
  private isInitialized: boolean = false;
  private stateGetter: (() => FullGameState) | null = null;

  public async init(): Promise<boolean> {
    if (this.isInitialized) return true;

    try {
      if (typeof window === 'undefined' || !window.indexedDB) {
        console.warn('[StorageService] IndexedDB unavailable in environment, switching to localStorage fallback.');
        this.isUsingFallback = true;
        this.isInitialized = true;
        this.bindLifecycleListeners();
        return true;
      }

      await new Promise<void>((resolve) => {
        const request = window.indexedDB.open(DB_NAME, DB_VERSION);

        request.onupgradeneeded = () => {
          const db = request.result;
          if (!db.objectStoreNames.contains(STORE_NAME)) {
            db.createObjectStore(STORE_NAME, { keyPath: 'key' });
          }
        };

        request.onsuccess = () => {
          this.db = request.result;
          this.isUsingFallback = false;
          resolve();
        };

        request.onerror = () => {
          console.warn('[StorageService] IndexedDB open error, falling back to localStorage:', request.error);
          this.isUsingFallback = true;
          resolve(); // Resolve to allow fallback mode
        };

        request.onblocked = () => {
          console.warn('[StorageService] IndexedDB open blocked by another tab.');
          this.isUsingFallback = true;
          resolve();
        };
      });
    } catch (err) {
      console.warn('[StorageService] Exception opening IndexedDB:', err);
      this.isUsingFallback = true;
    }

    this.isInitialized = true;
    this.bindLifecycleListeners();
    return true;
  }

  public registerStateGetter(getter: () => FullGameState): void {
    this.stateGetter = getter;
  }

  public async loadGameState(): Promise<FullGameState> {
    if (!this.isInitialized) {
      await this.init();
    }

    let loadedState: FullGameState | null = null;

    // 1. Attempt IndexedDB Load
    if (!this.isUsingFallback && this.db) {
      try {
        const record = await new Promise<any>((resolve, reject) => {
          const tx = this.db!.transaction(STORE_NAME, 'readonly');
          const store = tx.objectStore(STORE_NAME);
          const req = store.get(SAVE_KEY);
          req.onsuccess = () => resolve(req.result);
          req.onerror = () => reject(req.error);
        });

        if (record) {
          if (record.data && typeof record.data === 'object' && record.data.stats) {
            loadedState = record.data;
          } else if (record.stats) {
            const { key, data, updatedAt, ...rest } = record;
            loadedState = rest as FullGameState;
          }
        }
      } catch (err) {
        console.warn('[StorageService] IndexedDB read error, falling back to localStorage:', err);
      }
    }

    // 2. Fallback to LocalStorage
    if (!loadedState && typeof localStorage !== 'undefined') {
      try {
        const raw = localStorage.getItem(LOCAL_STORAGE_KEY);
        if (raw) {
          const parsed = JSON.parse(raw);
          if (parsed && typeof parsed === 'object') {
            loadedState = parsed.data || parsed;
          }
        }
      } catch (err) {
        console.warn('[StorageService] LocalStorage load error:', err);
      }
    }

    // 3. Fallback to Seed Initialization
    if (!loadedState) {
      console.log('[StorageService] No existing save found. Creating default game state.');
      loadedState = createDefaultGameState();
      await this.saveGameState(loadedState);
    } else {
      // Migrate loaded state to latest schema version and sanitize properties
      loadedState = migrateGameState(loadedState);
      // Apply offline delta rehydration
      const rehydration = applyOfflineRehydration(loadedState);
      loadedState = rehydration.state;
      console.log('[StorageService] Offline rehydration complete:', rehydration.result);
    }

    this.cachedState = loadedState;
    return loadedState;
  }

  public async saveGameState(state: FullGameState): Promise<void> {
    this.cachedState = state;
    state.lastActiveTimestamp = Date.now();
    state.stats.lastTickTimestamp = Date.now();

    const record = {
      key: SAVE_KEY,
      data: state,
      updatedAt: Date.now(),
      ...state,
    };

    // 1. Primary IndexedDB write
    if (!this.isUsingFallback && this.db) {
      try {
        await new Promise<void>((resolve, reject) => {
          const tx = this.db!.transaction(STORE_NAME, 'readwrite');
          const store = tx.objectStore(STORE_NAME);
          const req = store.put(record);
          tx.oncomplete = () => resolve();
          tx.onerror = () => reject(tx.error);
          req.onerror = () => reject(req.error);
        });
      } catch (err) {
        console.warn('[StorageService] IndexedDB write failed, falling back to localStorage:', err);
        this.isUsingFallback = true;
      }
    }

    // 2. Secondary LocalStorage snapshot mirror
    if (typeof localStorage !== 'undefined') {
      try {
        localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(record));
      } catch (err) {
        console.warn('[StorageService] LocalStorage backup write error (quota or blocked):', err);
      }
    }
  }

  public scheduleAutoSave(state?: FullGameState): void {
    if (state) {
      this.cachedState = state;
    }

    if (this.debounceTimer !== null) {
      clearTimeout(this.debounceTimer);
    }

    this.debounceTimer = setTimeout(async () => {
      this.debounceTimer = null;
      const targetState = this.cachedState || (this.stateGetter ? this.stateGetter() : null);
      if (targetState) {
        await this.saveGameState(targetState);
      }
    }, 1000);
  }

  public startHeartbeat(intervalMs: number = 10000): void {
    this.stopHeartbeat();
    this.heartbeatInterval = setInterval(async () => {
      const targetState = this.cachedState || (this.stateGetter ? this.stateGetter() : null);
      if (targetState) {
        await this.saveGameState(targetState);
      }
    }, intervalMs);
  }

  public stopHeartbeat(): void {
    if (this.heartbeatInterval !== null) {
      clearInterval(this.heartbeatInterval);
      this.heartbeatInterval = null;
    }
  }

  public async resetProgress(): Promise<void> {
    const newState = createDefaultGameState();
    this.cachedState = newState;

    if (!this.isUsingFallback && this.db) {
      try {
        await new Promise<void>((resolve, reject) => {
          const tx = this.db!.transaction(STORE_NAME, 'readwrite');
          const store = tx.objectStore(STORE_NAME);
          store.delete(SAVE_KEY);
          tx.oncomplete = () => resolve();
          tx.onerror = () => reject(tx.error);
        });
      } catch (err) {
        console.warn('[StorageService] IndexedDB reset error:', err);
      }
    }

    if (typeof localStorage !== 'undefined') {
      try {
        localStorage.removeItem(LOCAL_STORAGE_KEY);
      } catch (err) {
        console.warn('[StorageService] LocalStorage reset error:', err);
      }
    }

    await this.saveGameState(newState);
  }

  public flushSyncSave(): void {
    const state = this.cachedState || (this.stateGetter ? this.stateGetter() : null);
    if (!state) return;

    state.lastActiveTimestamp = Date.now();
    state.stats.lastTickTimestamp = Date.now();

    const record = {
      key: SAVE_KEY,
      data: state,
      updatedAt: Date.now(),
      ...state,
    };

    // Synchronous localStorage write guarantees flush before iOS process freeze
    if (typeof localStorage !== 'undefined') {
      try {
        localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(record));
      } catch (err) {
        console.warn('[StorageService] Lifecycle sync save error:', err);
      }
    }

    // Attempt async write if possible
    if (!this.isUsingFallback && this.db) {
      try {
        const tx = this.db.transaction(STORE_NAME, 'readwrite');
        tx.objectStore(STORE_NAME).put(record);
      } catch {
        // Ignored during page unload
      }
    }
  }

  private bindLifecycleListeners(): void {
    if (typeof window === 'undefined') return;

    window.addEventListener('pagehide', () => this.flushSyncSave());
    window.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') {
        this.flushSyncSave();
      }
    });
    window.addEventListener('beforeunload', () => this.flushSyncSave());
  }

  public getUsingFallback(): boolean {
    return this.isUsingFallback;
  }

  /**
   * Exports a portable Base64 save code ("POCKETLIFE_V1:...") for transferring progress to a new phone.
   */
  public exportSaveCode(state?: FullGameState): string {
    const target = state || this.cachedState || (this.stateGetter ? this.stateGetter() : createDefaultGameState());
    const json = JSON.stringify(target);
    const encoded =
      typeof btoa === 'function'
        ? btoa(encodeURIComponent(json))
        : Buffer.from(encodeURIComponent(json), 'utf-8').toString('base64');
    return `POCKETLIFE_V1:${encoded}`;
  }

  /**
   * Imports and validates a portable Base64 save code ("POCKETLIFE_V1:...") or raw JSON string,
   * persists it to IndexedDB + localStorage, and returns the restored FullGameState.
   */
  public async importSaveCode(codeOrJson: string): Promise<FullGameState> {
    if (!codeOrJson || typeof codeOrJson !== 'string' || !codeOrJson.trim()) {
      throw new Error('Mã dữ liệu trống hoặc không hợp lệ.');
    }

    const raw = codeOrJson.trim();
    let jsonStr = raw;

    if (raw.startsWith('POCKETLIFE_V1:')) {
      const b64 = raw.slice('POCKETLIFE_V1:'.length).trim();
      const decodedUri =
        typeof atob === 'function'
          ? atob(b64)
          : Buffer.from(b64, 'base64').toString('utf-8');
      jsonStr = decodeURIComponent(decodedUri);
    }

    const parsed = JSON.parse(jsonStr);
    if (!parsed || typeof parsed !== 'object' || (!parsed.stats && !parsed.data?.stats)) {
      throw new Error('Cấu trúc dữ liệu không đúng chuẩn American Dream: Pocket Life.');
    }

    const statePayload = parsed.data && parsed.data.stats ? parsed.data : parsed;
    const migrated = migrateGameState(statePayload);
    await this.saveGameState(migrated);
    return migrated;
  }
}

export const storageService = new StorageService();
